export type ConsoleLevel = 'log' | 'warn' | 'error' | 'info' | 'debug';

export interface ConsoleLogEntry {
  id: string;
  level: ConsoleLevel;
  message: string;
  timestamp: string;
  stack: string | null;
}

const MAX_ENTRIES = 100;
const MESSAGE_LIMIT = 2048;
const LEVELS: ConsoleLevel[] = ['log', 'warn', 'error', 'info', 'debug'];

// В production вывод в видимую консоль браузера отключён (безопасность: не раскрываем
// внутреннюю логику, данные и стектрейсы). Логи продолжают буферизироваться для баг-репортов.
const SILENCE_BROWSER_CONSOLE = import.meta.env.PROD;

let buffer: ConsoleLogEntry[] = [];
let patched = false;

function serializeArgs(args: unknown[]): string {
  return args.map(a => {
    if (typeof a === 'string') return a;
    try { return JSON.stringify(a); }
    catch { return String(a); }
  }).join(' ');
}

export function initConsoleLogger(): void {
  if (patched) return;
  patched = true;

  for (const level of LEVELS) {
    const original = console[level].bind(console);

    (console as unknown as Record<string, (...args: unknown[]) => void>)[level] = (...args: unknown[]) => {
      if (!SILENCE_BROWSER_CONSOLE) {
        original(...args);
      }

      let message: string;
      try {
        message = serializeArgs(args);
      } catch {
        message = '[unserializable]';
      }
      if (message.length > MESSAGE_LIMIT) {
        message = message.slice(0, MESSAGE_LIMIT) + '...[truncated]';
      }

      let stack: string | null = null;
      if (level === 'error') {
        const errArg = args.find(a => a instanceof Error) as Error | undefined;
        stack = errArg?.stack ?? null;
      }

      const entry: ConsoleLogEntry = {
        id: crypto.randomUUID(),
        level,
        message,
        timestamp: new Date().toISOString(),
        stack,
      };

      if (buffer.length >= MAX_ENTRIES) buffer.shift();
      buffer.push(entry);
    };
  }
}

export function getConsoleLogs(): ConsoleLogEntry[] {
  return [...buffer];
}

export function clearConsoleLogs(): void {
  buffer = [];
}
